/**
 * Standards Service
 *
 * Fetch API implementation for experience template standards.
 * Migrated from: components/experienceTemplates/standardServices.js
 */

const API_SERVER = process.env.API_SERVER || '';

export interface Standard {
  id: number;
  expectation_code: string;
  expectation_label: string;
}

export interface Breakout {
  expectation_id: number;
  breakout_code: string;
  breakout_label: string;
  breakout_order: number;
}

export interface StandardWithBreakouts {
  expectation: Standard;
  breakouts: Breakout[];
}

/**
 * Fetches template expectations for a given template, standard set, and grade.
 */
export async function getTemplateExpectations(
  templateId: number,
  setId: number,
  gradeId: number
): Promise<Standard[]> {
  if (!templateId || !setId || !gradeId) {
    throw new Error('templateId, setId, and gradeId are required and must be valid numbers');
  }

  const response = await fetch(
    `${API_SERVER}/experience_templates/${templateId}/set/${setId}/grade/${gradeId}/expectations`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch template expectations: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetches template breakouts for a given template.
 */
export async function getTemplateBreakouts(templateId: number): Promise<Breakout[]> {
  if (!templateId) {
    throw new Error('templateId is required and must be a valid number');
  }

  const response = await fetch(`${API_SERVER}/experience_templates/${templateId}/breakouts`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch template breakouts: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetches scene expectations for a given template, standard set, and grade.
 */
export async function getSceneExpectations(
  templateId: number,
  setId: number,
  gradeId: number
): Promise<unknown> {
  if (!templateId || !setId || !gradeId) {
    throw new Error('templateId, setId, and gradeId are required and must be valid numbers');
  }

  const response = await fetch(
    `${API_SERVER}/experience_templates/${templateId}/set/${setId}/grade/${gradeId}/scene_expectations`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch scene expectations: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetches element expectations for a given template, element, standard set, and grade.
 */
export async function getElementExpectations(
  templateId: number,
  elementId: number,
  setId: number,
  gradeId: number
): Promise<Standard[]> {
  if (!templateId || !elementId || !setId || !gradeId) {
    throw new Error('templateId, elementId, setId, and gradeId are required and must be valid numbers');
  }

  const response = await fetch(
    `${API_SERVER}/experience_templates/${templateId}/elements/${elementId}/set/${setId}/grade/${gradeId}/expectations`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch element expectations: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

const StandardsService = {
  getTemplateExpectations,
  getTemplateBreakouts,
  getSceneExpectations,
  getElementExpectations,
};

export default StandardsService;
